import { supabase } from '../lib/supabase';

// Moodboards de la marca. Los crea la plataforma (Atlas) y el editor de video
// sólo los LEE, para poder meter una referencia en el video sin salir a
// buscarla afuera.
//
// Esquema (ya existente en la base):
//   table  : public.moodboards          → id, id_company, brand_id, nombre, color, created_at
//   table  : public.moodboard_imagenes  → id, moodboard_id, id_company, nombre_archivo, orden, created_at
//   bucket : 'moodboards' (privado)
//   path   : `${id_company}/${moodboard_id}/${nombre_archivo}`
// RLS: las tres cosas (las dos tablas y los objetos del bucket) filtran por la
// empresa del usuario, así que alcanza con pedir por `brand_id`.

export interface ImagenDeMoodboard {
  id: string;
  /** Nombre del tablero al que pertenece — se muestra sobre la miniatura. */
  moodboard: string;
  nombreArchivo: string;
  orden: number;
  /** URL firmada, ya resuelta. */
  url?: string;
}

const BUCKET = 'moodboards';
const FIRMA_SEGUNDOS = 3600;

/**
 * Todas las imágenes de todos los moodboards de una marca, en un solo listado:
 * tablero más nuevo primero y, dentro de cada uno, por `orden`.
 *
 * Devuelve `[]` ante cualquier error (la marca puede no tener ninguno, y una
 * carpeta vacía no es motivo para romper el panel).
 */
export async function fetchImagenesDeMoodboards(
  brandId: string,
): Promise<ImagenDeMoodboard[]> {
  const { data: tableros, error: errTableros } = await supabase
    .from('moodboards')
    .select('id, nombre, created_at')
    .eq('brand_id', brandId)
    .order('created_at', { ascending: false });
  if (errTableros || !tableros || tableros.length === 0) {
    if (errTableros) console.error('[moodboards] fetch tableros', errTableros);
    return [];
  }

  const nombrePorTablero = new Map<string, string>(
    (tableros as Array<{ id: string; nombre: string | null }>).map((t) => [
      t.id,
      t.nombre || 'Estilo',
    ]),
  );
  const orden = new Map<string, number>(
    (tableros as Array<{ id: string }>).map((t, i) => [t.id, i]),
  );

  const { data: filas, error: errImgs } = await supabase
    .from('moodboard_imagenes')
    .select('id, moodboard_id, id_company, nombre_archivo, orden, created_at')
    .in('moodboard_id', Array.from(nombrePorTablero.keys()))
    .order('orden', { ascending: true })
    .order('created_at', { ascending: true });
  if (errImgs || !filas || filas.length === 0) {
    if (errImgs) console.error('[moodboards] fetch imágenes', errImgs);
    return [];
  }

  type Fila = {
    id: string;
    moodboard_id: string;
    id_company: string;
    nombre_archivo: string;
    orden: number | null;
  };
  const items = (filas as Fila[])
    .slice()
    // El orden de los tableros manda sobre el de las imágenes: primero todo lo
    // del tablero más nuevo, y recién ahí el siguiente.
    .sort(
      (a, b) =>
        (orden.get(a.moodboard_id) ?? 0) - (orden.get(b.moodboard_id) ?? 0),
    );

  // Una firma por imagen, en paralelo. `createSignedUrls` firma de a lote pero
  // pide todos los paths del MISMO bucket y devuelve el resultado en orden;
  // con una llamada por path el manejo de errores queda por imagen.
  return await Promise.all(
    items.map(async (f): Promise<ImagenDeMoodboard> => {
      const path = `${f.id_company}/${f.moodboard_id}/${f.nombre_archivo}`;
      const { data } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(path, FIRMA_SEGUNDOS);
      return {
        id: f.id,
        moodboard: nombrePorTablero.get(f.moodboard_id) || 'Estilo',
        nombreArchivo: f.nombre_archivo,
        orden: f.orden ?? 0,
        url: data?.signedUrl,
      };
    }),
  );
}
